import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { format } from "date-fns";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetAllDirectionsForSelectLazy } from "~/entity/Directions";
import { PositionStatusRest, useGetPositionById } from "~/entity/Positions";
import { useGetLocationData } from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import { FormSubmit } from "~/pages/Editor/EditorDirectories/types";
import { OpenModal } from "~/pages/Editor/type";
import { Field } from "~/shared/ui/Field";

import { defaultValues, positionQueryManagement } from "./options";
import { schema } from "./validation";

type Props = {
  closeModal: () => void;
  refetch: any;
  formId: string;
};

const PositionManagement = ({ closeModal, refetch, formId }: Props) => {
  const { t } = useTranslation();
  const { currentId, openModal } = useGetLocationData();
  const isEdit = openModal === OpenModal.Edit;

  const { position } = useGetPositionById(currentId, isEdit);

  const {
    directions,
    searchText: searchDirections,
    isLoading: isLoadingDirections,
  } = useGetAllDirectionsForSelectLazy({
    ActualOn: format(new Date(), "yyyy-MM-dd"),
  });

  const directionOptions = directions.filter(
    ({ value }) => value !== undefined && value !== null,
  );

  const statusOptions = [
    { value: "active", label: t("positionDirectory.active") },
    { value: "inactive", label: t("positionDirectory.inactive") },
  ];

  const { editPosition, addPosition } = positionQueryManagement({
    refetch,
    closeModal,
  });

  const { handleSubmit, control, errors, reset } = useForm<FormSubmit>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isEdit && position) {
      reset({
        ...defaultValues,
        id: String(position.instanceId),
        name: position.name,
        code: position.code,
        status: {
          value: position.status || undefined,
          label: position.status
            ? t(`positionDirectory.${position.status}`)
            : "",
        },
        DirectionId: {
          // В опциях селекта направлений value — число (instanceId)
          value: (position.directionId || undefined) as unknown as string,
          label: position.direction?.name || "",
        },
        RequiredEmail: position.requiredEmail,
        showInZd: Boolean(position.showInZd),
        jobDescriptionIsRequired: Boolean(position.jobDescriptionIsRequired),
        updatedAt: getFormatDate(position.updatedAt, true),
        createdAt: getFormatDate(position.createdAt, true),
      });
    }
  }, [position, isEdit]);

  const submit: SubmitHandler<FormSubmit> = (data) => {
    const {
      code,
      name,
      status,
      DirectionId,
      RequiredEmail,
      showInZd,
      jobDescriptionIsRequired,
    } = data;

    const body = {
      code,
      name,
      status: status?.value as PositionStatusRest,
      directionId: Number(DirectionId?.value),
      requiredEmail: RequiredEmail,
      showInZd: Boolean(showInZd),
      jobDescriptionIsRequired: Boolean(jobDescriptionIsRequired),
    };

    if (isEdit) {
      editPosition(Number(currentId), body);
    } else if (openModal === OpenModal.Add) {
      addPosition(body);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(submit)}>
      <Flex vertical gap={10}>
        {isEdit && (
          <Field
            type="input"
            name="id"
            control={control}
            label={t("positionDirectory.id")}
            disabled
          />
        )}
        <Field
          type="input"
          name="name"
          control={control}
          label={t("positionDirectory.titlePosition")}
          error={errors["name"]?.message}
        />
        <Field
          type="input"
          name="code"
          control={control}
          label={t("positionDirectory.positionCode")}
          error={errors["code"]?.message}
        />
        <Field
          type="select"
          name="status"
          control={control}
          label={t("positionDirectory.status")}
          options={statusOptions}
          labelInValue
          error={errors["status"]?.label?.message}
        />
        <Field
          type="select"
          name="DirectionId"
          control={control}
          label={t("positionDirectory.belongingDirectionID")}
          options={directionOptions}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchDirections}
          loading={isLoadingDirections}
          error={errors["DirectionId"]?.label?.message}
        />
        {isEdit && (
          <>
            <Field
              type="input"
              name="updatedAt"
              control={control}
              label={t("positionDirectory.updatedAt")}
              disabled
            />
            <Field
              type="input"
              name="createdAt"
              control={control}
              label={t("positionDirectory.createdAt")}
              disabled
            />
          </>
        )}
        <Field
          type="checkbox"
          name="RequiredEmail"
          control={control}
          label={t("positionDirectory.RequiredEmail")}
        />
        <Field
          type="checkbox"
          name="jobDescriptionIsRequired"
          control={control}
          label={t("positionDirectory.jobDescriptionIsRequired")}
        />
        <Field
          type="checkbox"
          name="showInZd"
          control={control}
          label={t("positionDirectory.showInZd")}
        />
      </Flex>
    </form>
  );
};

export default PositionManagement;
